import React, { useEffect, useRef, useState } from 'react';

const COLUMNS = ['service', 'duration', 'status'];
const DATA = [
  ['checkout', '120ms', 'ok'],
  ['checkout', '340ms', 'error'],
  ['payments', '95ms', 'ok'],
];

const ROW_ACCENT = '#f43f5e';
const COL_ACCENT = '#00e5c8';

const RowVsColumnDemo: React.FC = () => {
  const [rowTouched, setRowTouched] = useState<Set<string>>(new Set());
  const [colTouched, setColTouched] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [showPunchline, setShowPunchline] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  const after = (delay: number, fn: () => void) => {
    timers.current.push(setTimeout(fn, delay));
  };

  const runDemo = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setRowTouched(new Set());
    setColTouched(new Set());
    setShowPunchline(false);
    setRunning(true);
    setHasRun(true);

    let t = 0;
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        t += 90;
        const key = `${r}-${c}`;
        after(t, () => setRowTouched(prev => new Set(prev).add(key)));
      }
      t += 60;
    }

    t += 260;
    for (let r = 0; r < 3; r++) {
      t += 200;
      const key = `${r}-1`;
      after(t, () => setColTouched(prev => new Set(prev).add(key)));
    }

    t += 200;
    after(t, () => { setShowPunchline(true); setRunning(false); });
  };

  return (
    <div style={{ margin: '28px 0 20px' }}>
      <div style={{
        fontSize: 13.5, color: 'var(--text2)', lineHeight: 1.6,
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)', padding: '14px 18px', marginBottom: 20,
      }}>
        The query: <code>SELECT avg(duration) FROM spans</code> — "what's the average span duration?"
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <DemoPanel
          label="Row store" tag="Postgres / MySQL" accent={ROW_ACCENT}
          desc="Each span is one contiguous record on disk. Reading &ldquo;duration&rdquo; means opening the whole record."
          touched={rowTouched}
        />
        <DemoPanel
          label="Column store" tag="ClickHouse" accent={COL_ACCENT}
          desc="Each field is its own contiguous strip. Reading &ldquo;duration&rdquo; touches only that strip."
          touched={colTouched}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', margin: '22px 0 4px' }}>
        <button onClick={runDemo} disabled={running} style={{
          background: 'var(--cyan)', color: '#080b10', border: 'none', fontWeight: 700,
          fontSize: 13.5, padding: '11px 22px', borderRadius: 'var(--radius-sm)',
          cursor: running ? 'default' : 'pointer', opacity: running ? 0.6 : 1,
          fontFamily: 'var(--font-body)', transition: 'opacity 0.2s',
        }}>
          {hasRun ? '↺ Run it again' : '▶ Run the query and watch what gets touched'}
        </button>
      </div>

      {showPunchline && (
        <p style={{
          fontSize: 14, lineHeight: 1.75, color: 'var(--text2)',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderLeft: `3px solid ${COL_ACCENT}`, borderRadius: 12,
          padding: '18px 22px', marginTop: 20,
        }}>
          Same question, same data. Row store touched <strong style={{ color: ROW_ACCENT }}>all 9 cells</strong> — it can't grab just "duration" without opening every full record.
          Column store touched <strong style={{ color: COL_ACCENT }}>3 cells</strong> — only the strip it needed. Now scale this from 3 spans to <strong style={{ color: 'var(--text)' }}>50 million</strong>, running on every dashboard load. That gap is why SigNoz, Datadog, and Honeycomb all build on a column store.
        </p>
      )}
    </div>
  );
};

const DemoPanel: React.FC<{ label: string; tag: string; desc: string; accent: string; touched: Set<string> }> = ({ label, tag, desc, accent, touched }) => (
  <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 18 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, margin: 0 }}>{label}</h4>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 10, padding: '2px 8px', borderRadius: 6,
        color: accent, background: `${accent}18`, border: `1px solid ${accent}30`,
      }}>{tag}</span>
    </div>
    <p style={{ fontSize: 12.5, color: 'var(--text3)', lineHeight: 1.6, margin: '0 0 16px' }}>{desc}</p>
    <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
      <thead>
        <tr>
          {COLUMNS.map(c => (
            <th key={c} style={{ border: '1px solid var(--border)', padding: '6px 8px', textAlign: 'left', color: 'var(--text3)', background: 'var(--surface)', fontWeight: 500 }}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {DATA.map((row, r) => (
          <tr key={r}>
            {row.map((val, c) => {
              const isTouched = touched.has(`${r}-${c}`);
              return (
                <td key={c} style={{
                  border: '1px solid var(--border)', padding: '6px 8px', color: 'var(--text)',
                  background: isTouched ? `${accent}22` : 'transparent',
                  boxShadow: isTouched ? `inset 0 0 0 1px ${accent}` : 'none',
                  transition: 'background 0.25s ease, box-shadow 0.25s ease',
                }}>{val}</td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--border)' }}>
      <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>Cells touched</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700, color: accent }}>
        {touched.size}<span style={{ color: 'var(--text3)', fontSize: 11, fontWeight: 400 }}> / 9</span>
      </span>
    </div>
  </div>
);

export default RowVsColumnDemo;
